import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import "remixicon/fonts/remixicon.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";


import "./styles/animations.css";


const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    "#0067E3";
  const appColorDarkLocalStorage =
    localStorage.getItem("primaryColorDark") ||
    "#02B881";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorDarkLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

const theme = useMemo(
    () =>
      createTheme(
        {
          // Scrollbar styles melhorados usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              display: "none", // Mantemos oculto para o look "App", mas se quiser mostrar mude para "8px"
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#2a2a2a",
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              display: "none",
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#E0E0E0" : "#404040",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#BDBDBD" : "#505050",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          // 🎨 PALETA EXACTA DEFINIDA PELO DESIGN
          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark, 
              light: mode === "light" ? `${primaryColorLight}80` : `${primaryColorDark}80`,
              dark: mode === "light" ? `${primaryColorLight}CC` : `${primaryColorDark}CC`,
              contrastText: "#ffffff",
            },
            secondary: { main: "#0066ff" },
            
            // ==========================================================
            // 🔥 HIGHLIGHT: CORES DO MODO ESCURO (DARK MODE) AQUI 👇
            // Modifique o SEGUNDO código hexadecimal de cada linha
            // ==========================================================
            background: {
              default: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#090a0c", // BG Geral / Chat inativo
              paper: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",   // BG Ticket e Chat
            },
            divider: mode === "light" ? "rgba(0, 0, 0, 0.12)" : /* 🌑 ESCURO -> */ "#4b5563",
            text: {
              primary: mode === "light" ? "#1D1D1F" : /* 🌑 ESCURO -> */ "#FAFAFA",
              secondary: mode === "light" ? "#54656F" : /* 🌑 ESCURO -> */ "#2E2F2F",
            },
            
            // --- Variáveis do Menu Lateral ---
            buttonActive: mode === "light" ? "#FAFAFA" : /* 🌑 ESCURO -> */ "#2E2F2F",
            buttonActiveText: mode === "light" ? "#1D1D1F" : /* 🌑 ESCURO -> */ "#FAFAFA",
            buttonInactiveText: mode === "light" ? "#54656F" : /* 🌑 ESCURO -> */ "#2E2F2F",
            bordabox: mode === "light" ? "#FAFAFA" : /* 🌑 ESCURO -> */ "#4b5563",
            tabHeaderBackground: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#0b0b0d",
            
            // --- Variáveis Legadas OBRIGATÓRIAS (Proteção do Sistema) ---
            dark: { main: mode === "light" ? "#1D1D1F" : /* 🌑 ESCURO -> */ "#FAFAFA" },
            light: { main: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#1D1D1F" },
            textPrimary: mode === "light" ? "#1D1D1F" : /* 🌑 ESCURO -> */ "#FAFAFA",
            borderPrimary: mode === "light" ? "#FAFAFA" : /* 🌑 ESCURO -> */ "#4b5563",
            optionsBackground: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d", // Menu Lateral
            options: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            fancyBackground: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#090a0c",
            newmessagebox: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#0b0b0d",
            inputdigita: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0c0c10",
            contactdrawer: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            announcements: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            login: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#090a0c",
            announcementspopover: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            chatlist: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0c0c10", // Ticket BG
            boxlist: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#090a0c",
            boxchatlist: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0c0c10",
            total: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            messageIcons: mode === "light" ? "#54656F" : /* 🌑 ESCURO -> */ "#4b5563",
            inputBackground: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0c0c10",
            barraSuperior: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#0b0b0d", // Bg Nav Superior
            campaigntab: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d",
            mediainput: mode === "light" ? "#F0F2F5" : /* 🌑 ESCURO -> */ "#090a0c",
            // ==========================================================
          },

          typography: {
            fontFamily: ['"Poppins"', 'sans-serif'].join(','),
            allVariants: {
              fontFamily: '"Poppins", sans-serif',
            },
          },

          shape: {
            borderRadius: 8, 
          },
          
          overrides: {
            MuiCssBaseline: {
              '@global': {
                '*': {
                  fontFamily: '"Poppins", sans-serif !important',
                },
                body: {
                  fontFamily: '"Poppins", sans-serif !important',
                },
                button: {
                  fontFamily: '"Poppins", sans-serif !important',
                },
                input: {
                  fontFamily: '"Poppins", sans-serif !important',
                },
                textarea: {
                  fontFamily: '"Poppins", sans-serif !important',
                },
              },
            },
            MuiTypography: {
              root: {
                fontFamily: '"Poppins", sans-serif !important',
              },
            },
            MuiInputBase: {
              root: {
                fontFamily: '"Poppins", sans-serif !important',
              },
              input: {
                fontFamily: '"Poppins", sans-serif !important',
              },
            },
            MuiButton: {
              root: {
                fontFamily: '"Poppins", sans-serif !important',
                borderRadius: 14,
                textTransform: 'none',
                fontWeight: 400,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: { maxWidth: 'none !important' },
              maxWidthMd: { maxWidth: 'none !important' },
              maxWidthSm: { maxWidth: 'none !important' },
              maxWidthXl: { maxWidth: 'none !important' },
              maxWidthXs: { maxWidth: 'none !important' },
            },
          
            MuiPaper: {
              root: {
                backgroundImage: 'none',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
                //boxShadow: "none", // Mantendo limpo igual layout anterior
              },
              rounded: {
                borderRadius: 10,
              },
              elevation1: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0)' },
              elevation2: { boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' },
              elevation3: { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }
            },

            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
              }
            },

            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
              }
            },

            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? "#ccc" : "#555",
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: mode === "light" 
                    ? `${primaryColorLight}08`
                    : `${primaryColorDark}08`,
                },
                '&.Mui-selected': {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                }
              }
            },

            MuiChip: {
              root: {
                // Background dos Tags de Tickets
                backgroundColor: mode === "light" ? "#e0e0e0" : "#06060c",
              }
            },

            MuiDrawer: {
              paper: {
                // 🔥 HIGHLIGHT: MODO ESCURO DO MENU LATERAL 👇
                backgroundColor: mode === "light" ? "#FFFFFF" : /* 🌑 ESCURO -> */ "#0b0b0d", // Menu Lateral BG
                borderRight: `1px solid ${mode === "light" ? "#FAFAFA" : /* 🌑 ESCURO -> */ "#161617"}`, // Borda solida
                
                width: 324,
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }
            },

            MuiAppBar: {
              root: {
                // 🔥 HIGHLIGHT: MODO ESCURO DA BARRA SUPERIOR 👇
                //backgroundColor: mode === "light" ? "#F0F2F5 !important" : /* 🌑 ESCURO -> */ "#29292A !important", // Bg Nav Superior
                boxShadow: "none",
              //borderBottom: `1px solid ${mode === "light" ? "#FAFAFA" : /* 🌑 ESCURO -> */ "#4b5563"}`,
                "& .MuiIconButton-root, & .MuiSvgIcon-root, & .MuiTypography-root": {
                //  color: mode === "light" ? "#FAFAFA !important" : /* 🌑 ESCURO -> */ "#FAFAFA !important",
                }
              },
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // DESABILITADO: Impedindo que o banco de dados (Whitelabel) sobrescreva as cores do nosso tema customizado.
    /*
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#25142DC7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
      
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#25142DC7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    */

    // MANTIDO: O sistema ainda pode buscar as Logos e o Nome, se você quiser alterá-los.
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });

    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });

    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });

    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "CliqBR"); // Fallback atualizado
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("CliqBR");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;